import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { CHANNEL_LABEL } from '../labels';
import type {
  Channel,
  PartnersLink,
  PartnersLinkQuery,
  PartnersReportQuery,
  ReportUnit,
} from '../types';

dayjs.extend(utc);
dayjs.extend(timezone);

type PeriodKey = 'ALL' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR';

const DATE_KEY_FORMAT = 'YYYY-MM-DD';

export const PERIODS: { key: PeriodKey; label: string; months: number }[] = [
  { key: 'ALL', label: '전체', months: 0 },
  { key: 'WEEK', label: '1주', months: 0 },
  { key: 'MONTH', label: '1개월', months: 1 },
  { key: 'QUARTER', label: '3개월', months: 3 },
  { key: 'YEAR', label: '1년', months: 12 },
];

export interface ReportFilter {
  channel: Channel | '';
  start: string;
  end: string;
  isAll: boolean;
}

const nowOf = () => dayjs().tz('Asia/Seoul');

export const todayOf = () => nowOf().format(DATE_KEY_FORMAT);

export const defaultFilterOf = (): ReportFilter => ({
  channel: '',
  start: todayOf(),
  end: todayOf(),
  isAll: true,
});

export const resolvedFilterOf = (filter: ReportFilter, firstDate: string) =>
  filter.isAll ? { ...filter, start: firstDate } : filter;

const UNIT_LIMIT = { day: 31, week: 14 };

export const dayCountOf = (start: string, end: string) =>
  dayjs(end).diff(dayjs(start), 'day') + 1;

export const firstDateOf = (links: Pick<PartnersLink, 'createAt'>[]) =>
  links
    .map((link) => dayjs(link.createAt).format(DATE_KEY_FORMAT))
    .toSorted()[0] ?? todayOf();

export const rangeOf = (periodKey: PeriodKey, firstDate?: string) => {
  const today = nowOf();
  const end = todayOf();

  if (periodKey === 'ALL') {
    return { start: firstDate ?? end, end, isAll: true };
  }

  const { months } =
    PERIODS.find((item) => item.key === periodKey) ?? PERIODS[0];
  const start =
    months === 0
      ? today.subtract(6, 'day')
      : today.subtract(months, 'month').add(1, 'day');

  return { start: start.format(DATE_KEY_FORMAT), end, isAll: false };
};

export const unitOf = (dayCount: number): ReportUnit => {
  if (dayCount <= UNIT_LIMIT.day) {
    return 'DAY';
  }

  if (Math.ceil(dayCount / 7) <= UNIT_LIMIT.week) {
    return 'WEEK';
  }

  return 'MONTH';
};

export const filterToLinkQuery = ({
  channel,
  start,
  end,
  isAll,
}: ReportFilter): PartnersLinkQuery => ({
  from: isAll ? undefined : start,
  to: end,
  channel: channel === '' ? undefined : channel,
});

export const filterToQuery = (
  filter: ReportFilter,
  unit: ReportUnit,
): PartnersReportQuery => ({ ...filterToLinkQuery(filter), unit });

export const rangeDatesOf = ({
  start,
  end,
}: Pick<ReportFilter, 'start' | 'end'>) => {
  const format = dayjs(start).isSame(end, 'year') ? 'MM.DD' : 'YY.MM.DD';

  return [dayjs(start).format(format), dayjs(end).format(format)];
};

export const rangeLabelOf = (filter: ReportFilter) =>
  rangeDatesOf(filter).join('~');

const isChannel = (value: unknown): value is Channel =>
  typeof value === 'string' && value in CHANNEL_LABEL;

const isDateKey = (value: unknown): value is string =>
  typeof value === 'string' && dayjs(value).format(DATE_KEY_FORMAT) === value;

export const filterToSearchParams = ({
  channel,
  start,
  end,
  isAll,
}: ReportFilter) => {
  const searchParams = new URLSearchParams({ start, end });

  if (channel) {
    searchParams.set('channel', channel);
  }

  if (isAll) {
    searchParams.set('all', '1');
  }

  return searchParams;
};

//* 뒤로·앞으로·새로고침에 고른 필터를 되살리되, router.replace의 서버 왕복은 하지 않는다
export const replaceFilterUrl = (filter: ReportFilter) => {
  history.replaceState(
    { historyIndex: history.state?.historyIndex },
    '',
    `${location.pathname}?${filterToSearchParams(filter)}${location.hash}`,
  );
};

export const filterOfSearchParams = ({
  channel,
  start,
  end,
  all,
}: Record<string, string | string[] | undefined>): ReportFilter => {
  if (!isDateKey(start) || !isDateKey(end) || start > end || end > todayOf()) {
    return defaultFilterOf();
  }

  return {
    channel: isChannel(channel) ? channel : '',
    start,
    end,
    isAll: all === '1',
  };
};

export const axisLabelOf = (bucketStartDate: string, unit: ReportUnit) =>
  dayjs(bucketStartDate).format(unit === 'MONTH' ? 'YY.MM' : 'MM.DD');

export const axisIndexesOf = (length: number, count: number) => [
  ...new Set(
    Array.from({ length: Math.min(count, length) }, (_, order) =>
      Math.round(
        (order * (length - 1)) / Math.max(Math.min(count, length) - 1, 1),
      ),
    ),
  ),
];

export const axisMaxOf = (value: number) => {
  const half = Math.max(value, 2) / 2;
  const magnitude = 10 ** Math.floor(Math.log10(half));
  const step =
    [1, 2, 5].map((times) => times * magnitude).find((item) => item >= half) ??
    magnitude * 10;

  return step * 2;
};

export const formatAxisCount = (value: number) =>
  value >= 10_000 && value % 10_000 === 0
    ? `${value / 10_000}만`
    : value.toLocaleString('ko-KR');
